import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import Breadcrumb from '../components/Breadcrumb';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { 
  ArrowLeft, Shield, Loader2, Save, Plus, Trash2, Edit, 
  Globe, Search, FileText, Link2, AlertTriangle, CheckCircle,
  Code, Map, RefreshCw, ExternalLink, Copy, BarChart3,
  Settings2, Layers, Eye, XCircle, ArrowRight
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stat Card Component
const StatCard = ({ icon: Icon, title, value, color = 'emerald' }) => {
  const colorClasses = {
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    red: 'bg-red-500/10 text-red-400 border-red-500/20',
    yellow: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  };

  return (
    <div className={`rounded-xl border p-4 ${colorClasses[color]}`}>
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5" />
        <div>
          <p className="text-zinc-400 text-sm">{title}</p>
          <p className="text-xl font-bold text-white">{value}</p>
        </div>
      </div>
    </div>
  );
};

const SEOManagementPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // State
  const [globalSettings, setGlobalSettings] = useState({});
  const [templates, setTemplates] = useState([]);
  const [redirects, setRedirects] = useState([]);
  const [notFoundLogs, setNotFoundLogs] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [audit, setAudit] = useState({ issues: [], warnings: [], passed: [], stats: {} });
  const [programmaticConfig, setProgrammaticConfig] = useState({});
  const [hreflangConfig, setHreflangConfig] = useState({});
  const [rankKeywords, setRankKeywords] = useState([]);
  const [rankChecking, setRankChecking] = useState(null); // null veya keyword id
  const [rankCheckingAll, setRankCheckingAll] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [sitemapConfig, setSitemapConfig] = useState({
    include_pages: true,
    include_categories: true,
    include_listings: true,
    include_blog: true,
    include_images: true,
    listings_priority: 0.7,
    blog_priority: 0.6,
    categories_priority: 0.8,
    pages_priority: 0.6
  });
  
  // Edit modals
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingRedirect, setEditingRedirect] = useState(null);
  const [editingSchema, setEditingSchema] = useState(null);
  const [newRedirectModal, setNewRedirectModal] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAllData();
    }
  }, [user, token]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [settingsRes, templatesRes, redirectsRes, logsRes, schemasRes, auditRes, progRes, hreflangRes, sitemapRes, rankRes] = await Promise.all([
        axios.get(`${API}/seo/settings`, { headers }),
        axios.get(`${API}/seo/templates`, { headers }),
        axios.get(`${API}/seo/redirects`, { headers }),
        axios.get(`${API}/seo/404-logs`, { headers }),
        axios.get(`${API}/seo/schemas`, { headers }),
        axios.get(`${API}/seo/audit`, { headers }),
        axios.get(`${API}/seo/programmatic-pages`, { headers }),
        axios.get(`${API}/seo/hreflang`, { headers }),
        axios.get(`${API}/seo/sitemap-config`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/seo/rank-keywords`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setGlobalSettings(settingsRes.data);
      setTemplates(templatesRes.data);
      setRedirects(redirectsRes.data);
      setNotFoundLogs(logsRes.data);
      setSchemas(schemasRes.data);
      setAudit(auditRes.data);
      setProgrammaticConfig(progRes.data);
      setHreflangConfig(hreflangRes.data);
      if (sitemapRes.data && Object.keys(sitemapRes.data).length > 0) {
        setSitemapConfig(sitemapRes.data);
      }
      setRankKeywords(rankRes.data || []);
    } catch (error) {
      console.error('Error fetching SEO data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveGlobalSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/seo/settings`, globalSettings, { headers });
      alert('Ayarlar kaydedildi!');
    } catch (error) {
      alert('Ayarlar kaydetme hatası');
    } finally {
      setSaving(false);
    }
  };

  const saveSitemapConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/seo/sitemap-config`, sitemapConfig, { headers });
      alert('Site haritası ayarları kaydedildi!');
    } catch (error) {
      alert('Site haritası ayarları kaydetme hatası');
    } finally {
      setSaving(false);
    }
  };

  const createRedirect = async (data) => {
    try {
      await axios.post(`${API}/seo/redirects`, data, { headers });
      fetchAllData();
      setNewRedirectModal(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Yönlendirme oluşturma hatası');
    }
  };

  const deleteRedirect = async (id) => {
    try {
      await axios.delete(`${API}/seo/redirects/${id}`, { headers });
      setRedirects(redirects.filter(r => r.id !== id));
    } catch (error) {
      alert('Yönlendirme silme hatası');
    }
  };

  const resolve404 = async (url, targetUrl = '') => {
    try {
      await axios.put(`${API}/seo/404-logs/${encodeURIComponent(url)}/resolve?target_url=${encodeURIComponent(targetUrl)}`, {}, { headers });
      fetchAllData();
    } catch (error) {
      alert('404 çözümleme hatası');
    }
  };

  const saveProgrammaticConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/seo/programmatic-pages`, programmaticConfig, { headers });
      alert('Ayarlar kaydedildi!');
    } catch (error) {
      alert('Ayarlar kaydetme hatası');
    } finally {
      setSaving(false);
    }
  };

  // ===== Rank Checker Functions =====
  const addRankKeyword = async () => {
    if (!newKeyword.trim()) return;
    try {
      const res = await axios.post(`${API}/seo/rank-keywords`, {
        keyword: newKeyword.trim(),
        domain: globalSettings.rank_checker_domain || ''
      }, { headers });
      setRankKeywords([res.data, ...rankKeywords]);
      setNewKeyword('');
    } catch (error) {
      alert(error.response?.data?.detail || 'Anahtar kelime eklenemedi');
    }
  };

  const deleteRankKeyword = async (id) => {
    try {
      await axios.delete(`${API}/seo/rank-keywords/${id}`, { headers });
      setRankKeywords(rankKeywords.filter(k => k.id !== id));
    } catch (error) {
      alert('Silme hatası');
    }
  };

  const checkKeywordRank = async (id) => {
    setRankChecking(id);
    try {
      const res = await axios.post(`${API}/seo/rank-check/${id}`, {}, { headers });
      setRankKeywords(rankKeywords.map(k =>
        k.id === id ? { ...k, last_position: res.data.position, last_url: res.data.url, last_checked: res.data.checked_at } : k
      ));
    } catch (error) {
      alert(error.response?.data?.detail || 'Sıralama kontrol hatası');
    } finally {
      setRankChecking(null);
    }
  };

  const checkAllRanks = async () => {
    setRankCheckingAll(true);
    try {
      await axios.post(`${API}/seo/rank-check-all`, {}, { headers });
      // Sonuçları yeniden çek
      const res = await axios.get(`${API}/seo/rank-keywords`, { headers });
      setRankKeywords(res.data || []);
    } catch (error) {
      alert(error.response?.data?.detail || 'Toplu kontrol hatası');
    } finally {
      setRankCheckingAll(false);
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center">
        <p className="text-zinc-400">Yönetici erişimi gerekli</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  const menuItems = [
    { id: 'dashboard', label: 'SEO Paneli', icon: BarChart3 },
    { id: 'global', label: 'Genel Ayarlar', icon: Settings2 },
    { id: 'templates', label: 'SEO Şablonları', icon: FileText },
    { id: 'redirects', label: 'Yönlendirmeler', icon: ArrowRight },
    { id: '404', label: '404 İzleme', icon: XCircle },
    { id: 'schemas', label: 'JSON-LD Şemaları', icon: Code },
    { id: 'serp', label: 'Önizleme & Kontrol', icon: Eye },
    { id: 'sitemap', label: 'Site Haritası & Robots', icon: Map },
    { id: 'programmatic', label: 'Programatik SEO', icon: Layers },
    { id: 'hreflang', label: 'Çoklu Dil', icon: Globe },
    { id: 'rank-checker', label: 'Sıra Bulucu', icon: Search },
  ];

  return (
    <div className="min-h-screen bg-zinc-800">
      {/* Breadcrumb */}
      <Breadcrumb items={[
        { label: 'Yönetim Paneli', href: '/admin' },
        { label: 'SEO Yönetimi', href: null }
      ]} />

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="flex items-center gap-2">
            <Search className="w-6 h-6 text-emerald-500" />
            <h1 className="text-2xl font-bold text-white">SEO Yönetimi</h1>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            <nav className="bg-zinc-900 rounded-lg border border-zinc-800 p-4 sticky top-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
              <div className="space-y-1">
                {menuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                      activeTab === item.id 
                        ? 'bg-emerald-600 text-white' 
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span className="text-sm">{item.label}</span>
                  </button>
                ))}
              </div>
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && (
              <div className="space-y-6">
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <h2 className="text-xl font-semibold text-white mb-4">SEO Denetim Özeti</h2>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <StatCard icon={AlertTriangle} title="Sorunlar" value={audit.issues?.length || 0} color="red" />
                    <StatCard icon={AlertTriangle} title="Uyarılar" value={audit.warnings?.length || 0} color="yellow" />
                    <StatCard icon={CheckCircle} title="Başarılı" value={audit.passed?.length || 0} color="emerald" />
                    <StatCard icon={Link2} title="Yönlendirmeler" value={audit.stats?.active_redirects || 0} color="blue" />
                  </div>

                  {/* Issues */}
                  {audit.issues?.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-red-400 font-medium mb-3">Kritik Sorunlar</h3>
                      <div className="space-y-2">
                        {audit.issues.map((issue, idx) => (
                          <div key={idx} className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-start gap-3">
                            <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-white">{issue.message}</p>
                              <p className="text-zinc-500 text-sm">Tür: {issue.type}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Warnings */}
                  {audit.warnings?.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-yellow-400 font-medium mb-3">Uyarılar</h3>
                      <div className="space-y-2">
                        {audit.warnings.map((warning, idx) => (
                          <div key={idx} className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-white">{warning.message}</p>
                              <p className="text-zinc-500 text-sm">Önem: {warning.severity}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Passed */}
                  {audit.passed?.length > 0 && (
                    <div>
                      <h3 className="text-emerald-400 font-medium mb-3">Başarılı Kontroller</h3>
                      <div className="space-y-2">
                        {audit.passed.map((item, idx) => (
                          <div key={idx} className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-emerald-400" />
                            <p className="text-white">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Global Settings Tab */}
            {activeTab === 'global' && (
              <div className="space-y-6">
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-white">Genel SEO Ayarları</h2>
                    <Button onClick={saveGlobalSettings} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Site Adı</Label>
                      <Input
                        value={globalSettings.site_name || ''}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, site_name: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Site Sloganı</Label>
                      <Input
                        value={globalSettings.site_tagline || ''}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, site_tagline: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                  </div>

                  <div className="mt-6 space-y-2">
                    <Label className="text-zinc-300">Varsayılan Başlık Şablonu</Label>
                    <Input
                      value={globalSettings.default_title_template || ''}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, default_title_template: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      placeholder="{page_title} | {site_name}"
                    />
                    <p className="text-zinc-500 text-sm">Değişkenler: {'{page_title}'}, {'{site_name}'}, {'{category}'}, {'{city}'}</p>
                  </div>

                  <div className="mt-6 space-y-2">
                    <Label className="text-zinc-300">Varsayılan Meta Açıklaması</Label>
                    <Textarea
                      value={globalSettings.default_meta_description || ''}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, default_meta_description: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      rows={3}
                    />
                  </div>

                  <div className="mt-6 space-y-2">
                    <Label className="text-zinc-300">Varsayılan Anahtar Kelimeler</Label>
                    <Input
                      value={globalSettings.default_keywords || ''}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, default_keywords: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      placeholder="keyword1, keyword2, keyword3"
                    />
                  </div>

                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Varsayılan OG Görsel URL</Label>
                      <Input
                        value={globalSettings.default_og_image || ''}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, default_og_image: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Twitter Hesabı</Label>
                      <Input
                        value={globalSettings.twitter_handle || ''}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, twitter_handle: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="@yourhandle"
                      />
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-zinc-800">
                    <h3 className="text-white font-medium mb-4">Analitik & Doğrulama</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Google Analytics ID</Label>
                        <Input
                          value={globalSettings.google_analytics_id || ''}
                          onChange={(e) => setGlobalSettings({ ...globalSettings, google_analytics_id: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder="G-XXXXXXXXXX"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Google Tag Manager ID</Label>
                        <Input
                          value={globalSettings.google_tag_manager_id || ''}
                          onChange={(e) => setGlobalSettings({ ...globalSettings, google_tag_manager_id: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder="GTM-XXXXXXX"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Google Search Console</Label>
                        <Input
                          value={globalSettings.google_search_console || ''}
                          onChange={(e) => setGlobalSettings({ ...globalSettings, google_search_console: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder="Doğrulama kodu"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Bing Doğrulama</Label>
                        <Input
                          value={globalSettings.bing_verification || ''}
                          onChange={(e) => setGlobalSettings({ ...globalSettings, bing_verification: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-zinc-800">
                    <h3 className="text-white font-medium mb-4">Google Sıra Bulucu Ayarları</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Custom Search API Key</Label>
                        <Input
                          value={globalSettings.google_custom_search_api_key || ''}
                          onChange={(e) => setGlobalSettings({ ...globalSettings, google_custom_search_api_key: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder="AIzaSy..."
                          type="password"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Custom Search Engine ID (CX)</Label>
                        <Input
                          value={globalSettings.google_custom_search_cx || ''}
                          onChange={(e) => setGlobalSettings({ ...globalSettings, google_custom_search_cx: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder="a1b2c3d4e5f6..."
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label className="text-zinc-300">Site Domain (Sıra Bulucu için)</Label>
                        <Input
                          value={globalSettings.rank_checker_domain || ''}
                          onChange={(e) => setGlobalSettings({ ...globalSettings, rank_checker_domain: e.target.value })}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder="example.com"
                        />
                        <p className="text-zinc-500 text-sm">Sıralama kontrolünde kullanılacak domain (protokol olmadan)</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-zinc-800">
                    <h3 className="text-white font-medium mb-4">Varsayılan Robots</h3>
                    <Select 
                      value={globalSettings.default_robots || 'index, follow'}
                      onValueChange={(v) => setGlobalSettings({ ...globalSettings, default_robots: v })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white w-64">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="index, follow" className="text-zinc-300">Index, Follow</SelectItem>
                        <SelectItem value="index, nofollow" className="text-zinc-300">Index, NoFollow</SelectItem>
                        <SelectItem value="noindex, follow" className="text-zinc-300">NoIndex, Follow</SelectItem>
                        <SelectItem value="noindex, nofollow" className="text-zinc-300">NoIndex, NoFollow</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="mt-8 pt-6 border-t border-zinc-800">
                    <h3 className="text-white font-medium mb-4">Özellik Ayarları</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white">JSON-LD Şemalarını Etkinleştir</p>
                          <p className="text-zinc-500 text-sm">Sayfalara yapısal veri ekle</p>
                        </div>
                        <Switch
                          checked={globalSettings.enable_json_ld !== false}
                          onCheckedChange={(checked) => setGlobalSettings({ ...globalSettings, enable_json_ld: checked })}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white">Open Graph Etkinleştir</p>
                          <p className="text-zinc-500 text-sm">Facebook/LinkedIn paylaşımı</p>
                        </div>
                        <Switch
                          checked={globalSettings.enable_open_graph !== false}
                          onCheckedChange={(checked) => setGlobalSettings({ ...globalSettings, enable_open_graph: checked })}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white">Twitter Cards Etkinleştir</p>
                          <p className="text-zinc-500 text-sm">Twitter paylaşım önizlemesi</p>
                        </div>
                        <Switch
                          checked={globalSettings.enable_twitter_cards !== false}
                          onCheckedChange={(checked) => setGlobalSettings({ ...globalSettings, enable_twitter_cards: checked })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Redirects Tab */}
            {activeTab === 'redirects' && (
              <div className="space-y-6">
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-white">301/302 Yönlendirmeler</h2>
                    <Button onClick={() => setNewRedirectModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Yönlendirme Ekle
                    </Button>
                  </div>

                  {redirects.length === 0 ? (
                    <p className="text-zinc-400 text-center py-8">Yapılandırılmış yönlendirme yok</p>
                  ) : (
                    <div className="space-y-3">
                      {redirects.map((redirect) => (
                        <div key={redirect.id} className="bg-zinc-800 rounded-lg p-4 flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={redirect.redirect_type === 301 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}>
                                {redirect.redirect_type}
                              </Badge>
                              {!redirect.is_active && <Badge className="bg-red-500/20 text-red-400">Pasif</Badge>}
                            </div>
                            <p className="text-white font-mono text-sm truncate">{redirect.source_url}</p>
                            <div className="flex items-center gap-2 text-zinc-400 text-sm">
                              <ArrowRight className="w-4 h-4" />
                              <span className="truncate">{redirect.target_url}</span>
                            </div>
                            <p className="text-zinc-500 text-xs mt-1">Tıklama: {redirect.hit_count || 0}</p>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingRedirect(redirect)}
                              className="text-zinc-400 hover:text-white"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-white">Yönlendirmeyi Sil</AlertDialogTitle>
                                  <AlertDialogDescription className="text-zinc-400">
                                    Bu yönlendirmeyi silmek istediğinize emin misiniz?
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="bg-zinc-800 text-zinc-300 border-zinc-700">İptal</AlertDialogCancel>
                                  <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteRedirect(redirect.id)}>
                                    Sil
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* New Redirect Modal */}
                {newRedirectModal && (
                  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-lg p-6">
                      <h3 className="text-xl font-bold text-white mb-4">Yönlendirme Ekle</h3>
                      <NewRedirectForm onSubmit={createRedirect} onCancel={() => setNewRedirectModal(false)} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 404 Monitor Tab */}
            {activeTab === '404' && (
              <div className="space-y-6">
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-white">404 Hata İzleme</h2>
                    <Button onClick={fetchAllData} variant="outline" className="border-zinc-700 text-zinc-300">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Yenile
                    </Button>
                  </div>

                  {notFoundLogs.length === 0 ? (
                    <p className="text-zinc-400 text-center py-8">Kayıtlı 404 hatası yok</p>
                  ) : (
                    <div className="space-y-3">
                      {notFoundLogs.filter(log => !log.is_resolved).map((log, idx) => (
                        <div key={idx} className="bg-zinc-800 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-mono text-sm truncate">{log.url}</p>
                              <div className="flex items-center gap-4 text-zinc-500 text-xs mt-1">
                                <span>Tıklama: {log.hit_count}</span>
                                <span>İlk: {new Date(log.first_seen).toLocaleDateString()}</span>
                                <span>Son: {new Date(log.last_seen).toLocaleDateString()}</span>
                              </div>
                              {log.referrer && (
                                <p className="text-zinc-500 text-xs mt-1">Yönlendiren: {log.referrer}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  const target = prompt('Yönlendirme hedef URL girin (sadece kapatmak için boş bırakın):');
                                  if (target !== null) {
                                    resolve404(log.url, target);
                                  }
                                }}
                                className="border-emerald-700 text-emerald-400"
                              >
                                Çözümle
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Sitemap & Robots Tab */}
            {activeTab === 'sitemap' && (
              <div className="space-y-6">
                {/* Sitemap Configuration */}
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-white">Site Haritası Yapılandırması</h2>
                    <Button onClick={saveSitemapConfig} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                    </Button>
                  </div>
                  <p className="text-zinc-400 text-sm mb-6">
                    Site haritanıza hangi içerik türlerinin dahil edileceğini yapılandırın. Değişiklikler kaydedildiğinde hemen uygulanır.
                  </p>
                  
                  {/* Content Types */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-blue-400" />
                        <div>
                          <p className="text-white font-medium">Sayfalar</p>
                          <p className="text-zinc-500 text-sm">Statik & dinamik sayfalar</p>
                        </div>
                      </div>
                      <Switch
                        checked={sitemapConfig.include_pages}
                        onCheckedChange={(checked) => setSitemapConfig({ ...sitemapConfig, include_pages: checked })}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Layers className="w-5 h-5 text-purple-400" />
                        <div>
                          <p className="text-white font-medium">Kategoriler</p>
                          <p className="text-zinc-500 text-sm">Kategori & şehir sayfaları</p>
                        </div>
                      </div>
                      <Switch
                        checked={sitemapConfig.include_categories}
                        onCheckedChange={(checked) => setSitemapConfig({ ...sitemapConfig, include_categories: checked })}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Eye className="w-5 h-5 text-emerald-400" />
                        <div>
                          <p className="text-white font-medium">İlanlar</p>
                          <p className="text-zinc-500 text-sm">Tüm aktif ilanlar</p>
                        </div>
                      </div>
                      <Switch
                        checked={sitemapConfig.include_listings}
                        onCheckedChange={(checked) => setSitemapConfig({ ...sitemapConfig, include_listings: checked })}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-yellow-400" />
                        <div>
                          <p className="text-white font-medium">Blog</p>
                          <p className="text-zinc-500 text-sm">Yayınlanan blog yazıları</p>
                        </div>
                      </div>
                      <Switch
                        checked={sitemapConfig.include_blog}
                        onCheckedChange={(checked) => setSitemapConfig({ ...sitemapConfig, include_blog: checked })}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg md:col-span-2">
                      <div className="flex items-center gap-3">
                        <Map className="w-5 h-5 text-pink-400" />
                        <div>
                          <p className="text-white font-medium">Görsel Site Haritası</p>
                          <p className="text-zinc-500 text-sm">Görseller için ayrı site haritası (alt metin ile)</p>
                        </div>
                      </div>
                      <Switch
                        checked={sitemapConfig.include_images}
                        onCheckedChange={(checked) => setSitemapConfig({ ...sitemapConfig, include_images: checked })}
                      />
                    </div>
                  </div>

                  {/* Priority Settings */}
                  <h3 className="text-white font-medium mb-4">Öncelik Ayarları</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-400 text-sm">İlanlar</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="1"
                        value={sitemapConfig.listings_priority}
                        onChange={(e) => setSitemapConfig({ ...sitemapConfig, listings_priority: parseFloat(e.target.value) })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400 text-sm">Blog</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="1"
                        value={sitemapConfig.blog_priority}
                        onChange={(e) => setSitemapConfig({ ...sitemapConfig, blog_priority: parseFloat(e.target.value) })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400 text-sm">Kategoriler</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="1"
                        value={sitemapConfig.categories_priority}
                        onChange={(e) => setSitemapConfig({ ...sitemapConfig, categories_priority: parseFloat(e.target.value) })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400 text-sm">Sayfalar</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="1"
                        value={sitemapConfig.pages_priority}
                        onChange={(e) => setSitemapConfig({ ...sitemapConfig, pages_priority: parseFloat(e.target.value) })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Sitemap Links */}
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <h2 className="text-xl font-semibold text-white mb-4">Site Haritası Dosyaları</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <a 
                      href={`${BACKEND_URL}/api/seo/sitemap-index.xml`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Map className="w-5 h-5 text-emerald-400" />
                        <span className="text-white">Site Haritası İndeksi</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-zinc-400" />
                    </a>
                    <a 
                      href={`${BACKEND_URL}/api/seo/sitemap.xml`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-blue-400" />
                        <span className="text-white">Ana Site Haritası</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-zinc-400" />
                    </a>
                    <a 
                      href={`${BACKEND_URL}/api/seo/sitemap-images.xml`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Map className="w-5 h-5 text-pink-400" />
                        <span className="text-white">Görsel Site Haritası</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-zinc-400" />
                    </a>
                  </div>
                </div>

                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-white">Robots.txt</h2>
                    <Button onClick={saveGlobalSettings} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                      <Save className="w-4 h-4 mr-2" />
                      Kaydet
                    </Button>
                  </div>
                  <Textarea
                    value={globalSettings.robots_txt || ''}
                    onChange={(e) => setGlobalSettings({ ...globalSettings, robots_txt: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
                    rows={12}
                  />
                  <div className="flex items-center gap-4 mt-4">
                    <a 
                      href={`${BACKEND_URL}/api/seo/robots.txt`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-emerald-400 hover:text-emerald-300 flex items-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Robots.txt Görüntüle
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Templates Tab */}
            {activeTab === 'templates' && (
              <div className="space-y-6">
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-white">SEO Şablonları</h2>
                    <Button onClick={() => setEditingTemplate({ name: '', page_type: 'listing', title_template: '', description_template: '', is_default: false })} className="bg-emerald-600 hover:bg-emerald-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Şablon Ekle
                    </Button>
                  </div>
                  <p className="text-zinc-400 text-sm mb-6">
                    SEO başlıkları ve açıklamaları otomatik oluşturmak için şablonlar oluşturun. {'{name}'}, {'{category}'}, {'{city}'}, {'{site_name}'} gibi değişkenler kullanın.
                  </p>

                  {templates.length === 0 ? (
                    <p className="text-zinc-500 text-center py-8">Henüz şablon yok</p>
                  ) : (
                    <div className="space-y-3">
                      {templates.map((template) => (
                        <div key={template.id} className="bg-zinc-800 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-white font-medium">{template.name}</h3>
                                <Badge className="bg-blue-500/20 text-blue-400">{template.page_type}</Badge>
                                {template.is_default && <Badge className="bg-emerald-500/20 text-emerald-400">Varsayılan</Badge>}
                              </div>
                              <p className="text-zinc-400 text-sm font-mono">{template.title_template}</p>
                              <p className="text-zinc-500 text-xs mt-1">{template.description_template}</p>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => setEditingTemplate(template)} className="text-zinc-400 hover:text-white">
                              <Edit className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Template Edit Modal */}
                {editingTemplate && (
                  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-lg p-6">
                      <h3 className="text-xl font-bold text-white mb-4">
                        {editingTemplate.id ? 'Şablonu Düzenle' : 'Yeni Şablon'}
                      </h3>
                      <TemplateForm 
                        template={editingTemplate} 
                        onSave={async (data) => {
                          if (editingTemplate.id) {
                            await axios.put(`${API}/seo/templates/${editingTemplate.id}`, data, { headers });
                          } else {
                            await axios.post(`${API}/seo/templates`, data, { headers });
                          }
                          fetchAllData();
                          setEditingTemplate(null);
                        }}
                        onCancel={() => setEditingTemplate(null)}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* JSON-LD Schemas Tab */}
            {activeTab === 'schemas' && (
              <div className="space-y-6">
                {/* Hazır Şema Şablonları */}
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <h2 className="text-xl font-semibold text-white mb-2">Hazır Şema Şablonları</h2>
                  <p className="text-zinc-500 text-sm mb-5">Hızlıca eklemek için bir şema türü seçin. İçerik otomatik doldurulacaktır.</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { type: 'Organization', label: 'Organizasyon', desc: 'Şirket/kuruluş bilgileri', icon: '🏢', template: JSON.stringify({"@context":"https://schema.org","@type":"Organization","name":"","url":"","logo":"","description":"","sameAs":[],"contactPoint":{"@type":"ContactPoint","telephone":"","contactType":"customer service","availableLanguage":"Turkish"}}, null, 2) },
                      { type: 'LocalBusiness', label: 'Yerel İşletme', desc: 'Fiziksel konum bilgileri', icon: '📍', template: JSON.stringify({"@context":"https://schema.org","@type":"LocalBusiness","name":"","image":"","url":"","telephone":"","address":{"@type":"PostalAddress","streetAddress":"","addressLocality":"","addressRegion":"","postalCode":"","addressCountry":"TR"},"openingHoursSpecification":[{"@type":"OpeningHoursSpecification","dayOfWeek":["Monday","Tuesday","Wednesday","Thursday","Friday"],"opens":"09:00","closes":"18:00"}],"priceRange":"$$"}, null, 2) },
                      { type: 'WebSite', label: 'Web Sitesi', desc: 'Site arama kutusu şeması', icon: '🌐', template: JSON.stringify({"@context":"https://schema.org","@type":"WebSite","name":"","url":"","potentialAction":{"@type":"SearchAction","target":{"@type":"EntryPoint","urlTemplate":"{search_url}?q={search_term_string}"},"query-input":"required name=search_term_string"}}, null, 2) },
                      { type: 'FAQPage', label: 'SSS Sayfası', desc: 'Sık sorulan sorular', icon: '❓', template: JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","mainEntity":[{"@type":"Question","name":"Soru 1?","acceptedAnswer":{"@type":"Answer","text":"Cevap 1."}},{"@type":"Question","name":"Soru 2?","acceptedAnswer":{"@type":"Answer","text":"Cevap 2."}}]}, null, 2) },
                      { type: 'Service', label: 'Hizmet', desc: 'Sunulan hizmet detayları', icon: '⚙️', template: JSON.stringify({"@context":"https://schema.org","@type":"Service","name":"","description":"","provider":{"@type":"Organization","name":""},"areaServed":{"@type":"Country","name":"Turkey"},"serviceType":"","offers":{"@type":"Offer","price":"","priceCurrency":"TRY"}}, null, 2) },
                      { type: 'Person', label: 'Kişi', desc: 'Freelancer/kişi profili', icon: '👤', template: JSON.stringify({"@context":"https://schema.org","@type":"Person","name":"","jobTitle":"","url":"","image":"","sameAs":[],"worksFor":{"@type":"Organization","name":""},"knowsAbout":[]}, null, 2) },
                      { type: 'HowTo', label: 'Nasıl Yapılır', desc: 'Adım adım rehber', icon: '📝', template: JSON.stringify({"@context":"https://schema.org","@type":"HowTo","name":"","description":"","step":[{"@type":"HowToStep","name":"Adım 1","text":"Açıklama 1"},{"@type":"HowToStep","name":"Adım 2","text":"Açıklama 2"}]}, null, 2) },
                      { type: 'Product', label: 'Ürün', desc: 'Ürün bilgisi ve fiyatı', icon: '📦', template: JSON.stringify({"@context":"https://schema.org","@type":"Product","name":"","description":"","image":"","brand":{"@type":"Brand","name":""},"offers":{"@type":"Offer","price":"","priceCurrency":"TRY","availability":"https://schema.org/InStock"},"aggregateRating":{"@type":"AggregateRating","ratingValue":"4.5","reviewCount":"10"}}, null, 2) },
                    ].map((preset) => (
                      <button
                        key={preset.type}
                        onClick={() => setEditingSchema({ name: preset.label, schema_type: preset.type, template: preset.template, is_active: true })}
                        className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 hover:border-emerald-500/30 rounded-xl p-4 text-left transition-all group"
                      >
                        <span className="text-2xl mb-2 block">{preset.icon}</span>
                        <h4 className="text-white font-medium text-sm group-hover:text-emerald-400 transition-colors">{preset.label}</h4>
                        <p className="text-zinc-500 text-xs mt-1">{preset.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Mevcut Şemalar */}
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-white">Kayıtlı Şemalar</h2>
                    <Button onClick={() => setEditingSchema({ name: '', schema_type: 'Organization', template: '{\n  "@context": "https://schema.org",\n  "@type": "Organization"\n}', is_active: true })} className="bg-emerald-600 hover:bg-emerald-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Boş Şema Ekle
                    </Button>
                  </div>

                  {schemas.length === 0 ? (
                    <div className="text-center py-12">
                      <Code className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                      <p className="text-zinc-500">Henüz şema eklenmemiş</p>
                      <p className="text-zinc-600 text-sm mt-1">Yukarıdaki hazır şablonlardan birini seçerek başlayın</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {schemas.map((schema) => {
                        let parsedTemplate = {};
                        try { parsedTemplate = JSON.parse(schema.template || '{}'); } catch {}
                        return (
                          <div key={schema.id} className="bg-zinc-800 rounded-lg p-4 hover:border-zinc-600 border border-transparent transition-colors">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <h3 className="text-white font-medium">{schema.name}</h3>
                                  <Badge className="bg-purple-500/20 text-purple-400 text-xs">{schema.schema_type}</Badge>
                                  {schema.is_active ? (
                                    <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">Aktif</Badge>
                                  ) : (
                                    <Badge className="bg-zinc-600/20 text-zinc-500 text-xs">Pasif</Badge>
                                  )}
                                </div>
                                <div className="bg-zinc-900 rounded-lg p-3 mt-2">
                                  <pre className="text-zinc-400 text-xs font-mono overflow-x-auto max-h-24 whitespace-pre-wrap">{JSON.stringify(parsedTemplate, null, 2).substring(0, 300)}{schema.template?.length > 300 ? '...' : ''}</pre>
                                </div>
                              </div>
                              <div className="flex gap-1 ml-3">
                                <Button variant="ghost" size="sm" onClick={() => setEditingSchema(schema)} className="text-zinc-400 hover:text-white">
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={async () => {
                                  if (window.confirm('Bu şemayı silmek istediğinize emin misiniz?')) {
                                    await axios.delete(`${API}/seo/schemas/${schema.id}`, { headers });
                                    fetchAllData();
                                  }
                                }} className="text-zinc-400 hover:text-red-400">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Schema Doğrulama İpuçları */}
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    Schema Doğrulama
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <a href="https://search.google.com/test/rich-results" target="_blank" rel="noopener noreferrer" className="bg-zinc-800 hover:bg-zinc-700 rounded-lg p-4 flex items-center gap-3 transition-colors group">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                        <Search className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium group-hover:text-emerald-400 transition-colors">Google Rich Results Test</p>
                        <p className="text-zinc-500 text-xs">Zengin sonuç uygunluğunu kontrol edin</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-zinc-600 ml-auto" />
                    </a>
                    <a href="https://validator.schema.org/" target="_blank" rel="noopener noreferrer" className="bg-zinc-800 hover:bg-zinc-700 rounded-lg p-4 flex items-center gap-3 transition-colors group">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                        <Code className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium group-hover:text-emerald-400 transition-colors">Schema.org Validator</p>
                        <p className="text-zinc-500 text-xs">JSON-LD yapısını doğrulayın</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-zinc-600 ml-auto" />
                    </a>
                  </div>
                </div>

                {/* Schema Edit Modal */}
                {editingSchema && (
                  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
                      <h3 className="text-xl font-bold text-white mb-4">
                        {editingSchema.id ? 'Şemayı Düzenle' : 'Yeni Şema'}
                      </h3>
                      <SchemaForm
                        schema={editingSchema}
                        onSave={async (data) => {
                          try {
                            JSON.parse(data.template);
                          } catch {
                            alert('Geçersiz JSON formatı! Lütfen JSON-LD şablonunu kontrol edin.');
                            return;
                          }
                          if (editingSchema.id) {
                            await axios.put(`${API}/seo/schemas/${editingSchema.id}`, data, { headers });
                          } else {
                            await axios.post(`${API}/seo/schemas`, data, { headers });
                          }
                          fetchAllData();
                          setEditingSchema(null);
                        }}
                        onCancel={() => setEditingSchema(null)}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* SERP Preview Tab */}
            {activeTab === 'serp' && (
              <div className="space-y-6">
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <h2 className="text-xl font-semibold text-white mb-2">Google SERP Önizleme</h2>
                  <p className="text-zinc-500 text-sm mb-6">Sayfanızın Google arama sonuçlarında nasıl görüneceğini test edin.</p>

                  <div className="space-y-4 mb-6">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Sayfa Başlığı</Label>
                      <Input
                        value={globalSettings.serp_preview_title || globalSettings.site_name || ''}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, serp_preview_title: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="Sayfa Başlığı | Site Adı"
                      />
                      <p className="text-zinc-600 text-xs">{(globalSettings.serp_preview_title || globalSettings.site_name || '').length}/60 karakter</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">URL</Label>
                      <Input
                        value={globalSettings.serp_preview_url || (typeof window !== 'undefined' ? window.location.origin : '')}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, serp_preview_url: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="https://example.com/sayfa"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Meta Açıklama</Label>
                      <Textarea
                        value={globalSettings.serp_preview_desc || globalSettings.default_meta_description || ''}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, serp_preview_desc: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        rows={3}
                        placeholder="Sayfa açıklaması..."
                      />
                      <p className="text-zinc-600 text-xs">{(globalSettings.serp_preview_desc || globalSettings.default_meta_description || '').length}/160 karakter</p>
                    </div>
                  </div>

                  {/* Desktop Preview */}
                  <div className="mb-6">
                    <h3 className="text-zinc-400 text-sm font-medium mb-3">Masaüstü Önizleme</h3>
                    <div className="bg-white rounded-xl p-6 max-w-2xl">
                      <div className="text-xs text-[#202124] flex items-center gap-1 mb-1">
                        <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4 rounded-full" onError={(e) => e.target.style.display='none'} />
                        <span>{(globalSettings.serp_preview_url || window.location.origin).replace('https://', '').replace('http://', '')}</span>
                      </div>
                      <h3 className="text-[#1a0dab] text-xl font-normal mb-1 hover:underline cursor-pointer leading-tight">
                        {(globalSettings.serp_preview_title || globalSettings.site_name || 'Sayfa Başlığı').substring(0, 60)}
                      </h3>
                      <p className="text-[#4d5156] text-sm leading-relaxed">
                        {(globalSettings.serp_preview_desc || globalSettings.default_meta_description || 'Meta açıklama burada görünecek...').substring(0, 160)}
                      </p>
                    </div>
                  </div>

                  {/* Mobile Preview */}
                  <div>
                    <h3 className="text-zinc-400 text-sm font-medium mb-3">Mobil Önizleme</h3>
                    <div className="bg-white rounded-xl p-4 max-w-sm">
                      <div className="text-xs text-[#202124] flex items-center gap-2 mb-1">
                        <div className="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold text-gray-600">
                          {(globalSettings.site_name || 'S').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-[13px]">{globalSettings.site_name || 'Site Adı'}</p>
                          <p className="text-[11px] text-gray-500">{(globalSettings.serp_preview_url || window.location.origin).replace('https://', '').replace('http://', '').substring(0, 40)}</p>
                        </div>
                      </div>
                      <h3 className="text-[#1a0dab] text-lg font-normal mt-2 hover:underline cursor-pointer leading-tight">
                        {(globalSettings.serp_preview_title || globalSettings.site_name || 'Sayfa Başlığı').substring(0, 60)}
                      </h3>
                      <p className="text-[#4d5156] text-[13px] leading-relaxed mt-1">
                        {(globalSettings.serp_preview_desc || globalSettings.default_meta_description || 'Meta açıklama burada görünecek...').substring(0, 120)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* OG / Social Media Preview */}
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <h2 className="text-xl font-semibold text-white mb-2">Sosyal Medya Önizleme</h2>
                  <p className="text-zinc-500 text-sm mb-6">Facebook, Twitter ve LinkedIn'de nasıl görüneceğini kontrol edin.</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Facebook/OG Preview */}
                    <div>
                      <h4 className="text-zinc-400 text-sm font-medium mb-3">Facebook / Open Graph</h4>
                      <div className="bg-[#f0f2f5] rounded-xl overflow-hidden max-w-sm">
                        <div className="aspect-[1.91/1] bg-zinc-300 flex items-center justify-center">
                          {globalSettings.default_og_image ? (
                            <img src={globalSettings.default_og_image} alt="OG" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-zinc-500 text-sm">OG Image (1200x630)</span>
                          )}
                        </div>
                        <div className="p-3">
                          <p className="text-[#65676b] text-xs uppercase">{(globalSettings.serp_preview_url || window.location.origin).replace('https://', '').replace('http://', '').split('/')[0]}</p>
                          <p className="text-[#1c1e21] font-semibold text-base mt-1 leading-tight">{(globalSettings.serp_preview_title || globalSettings.site_name || 'Sayfa Başlığı').substring(0, 60)}</p>
                          <p className="text-[#65676b] text-sm mt-1">{(globalSettings.serp_preview_desc || globalSettings.default_meta_description || 'Açıklama...').substring(0, 80)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Twitter Preview */}
                    <div>
                      <h4 className="text-zinc-400 text-sm font-medium mb-3">Twitter / X</h4>
                      <div className="bg-black rounded-2xl overflow-hidden max-w-sm border border-zinc-700">
                        <div className="aspect-[2/1] bg-zinc-800 flex items-center justify-center">
                          {globalSettings.default_og_image ? (
                            <img src={globalSettings.default_og_image} alt="Twitter" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-zinc-600 text-sm">Twitter Card Image</span>
                          )}
                        </div>
                        <div className="p-3">
                          <p className="text-white font-medium text-sm">{(globalSettings.serp_preview_title || globalSettings.site_name || 'Sayfa Başlığı').substring(0, 60)}</p>
                          <p className="text-zinc-500 text-xs mt-1">{(globalSettings.serp_preview_desc || globalSettings.default_meta_description || 'Açıklama...').substring(0, 100)}</p>
                          <p className="text-zinc-600 text-xs mt-1">{(globalSettings.serp_preview_url || window.location.origin).replace('https://', '').replace('http://', '').split('/')[0]}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SEO Checklist */}
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    SEO Kontrol Listesi
                  </h2>
                  <div className="space-y-2">
                    {[
                      { check: !!globalSettings.site_name, text: 'Site adı tanımlanmış' },
                      { check: !!globalSettings.default_meta_description, text: 'Varsayılan meta açıklama girilmiş' },
                      { check: !!globalSettings.default_og_image, text: 'Varsayılan OG görseli eklenmiş' },
                      { check: !!globalSettings.google_analytics_id, text: 'Google Analytics bağlanmış' },
                      { check: !!globalSettings.google_search_console, text: 'Google Search Console doğrulanmış' },
                      { check: globalSettings.enable_json_ld !== false, text: 'JSON-LD yapılandırılmış veri aktif' },
                      { check: globalSettings.enable_open_graph !== false, text: 'Open Graph etiketleri aktif' },
                      { check: globalSettings.enable_twitter_cards !== false, text: 'Twitter Cards aktif' },
                      { check: schemas.length > 0, text: 'En az bir JSON-LD şeması eklenmiş' },
                      { check: redirects.length > 0 || notFoundLogs.length === 0, text: '404 hataları yönetilmiş' },
                    ].map((item, idx) => (
                      <div key={idx} className={`flex items-center gap-3 rounded-lg p-3 ${item.check ? 'bg-emerald-500/5 border border-emerald-500/10' : 'bg-red-500/5 border border-red-500/10'}`}>
                        {item.check ? (
                          <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                        )}
                        <span className={`text-sm ${item.check ? 'text-zinc-300' : 'text-zinc-400'}`}>{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Programmatic SEO Tab */}
            {activeTab === 'programmatic' && (
              <div className="space-y-6">
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-white">Programatik SEO Sayfaları</h2>
                    <Button onClick={saveProgrammaticConfig} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Kaydediliyor...' : 'Kaydet'}
                    </Button>
                  </div>
                  <p className="text-zinc-400 text-sm mb-6">
                    Kategori + şehir kombinasyonları için SEO optimize edilmiş açılış sayfalarını otomatik oluşturun.
                  </p>

                  <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg mb-6">
                    <div>
                      <p className="text-white font-medium">Programatik Sayfaları Etkinleştir</p>
                      <p className="text-zinc-500 text-sm">/kategori/şehir açılış sayfaları oluştur</p>
                    </div>
                    <Switch
                      checked={programmaticConfig.enabled || false}
                      onCheckedChange={(checked) => setProgrammaticConfig({ ...programmaticConfig, enabled: checked })}
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Başlık Şablonu</Label>
                      <Input
                        value={programmaticConfig.title_template || ''}
                        onChange={(e) => setProgrammaticConfig({ ...programmaticConfig, title_template: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="{category} in {city} | {site_name}"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Meta Açıklama Şablonu</Label>
                      <Textarea
                        value={programmaticConfig.description_template || ''}
                        onChange={(e) => setProgrammaticConfig({ ...programmaticConfig, description_template: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        rows={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">H1 Şablonu</Label>
                      <Input
                        value={programmaticConfig.h1_template || ''}
                        onChange={(e) => setProgrammaticConfig({ ...programmaticConfig, h1_template: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="{category} in {city}"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">İçerik Şablonu</Label>
                      <Textarea
                        value={programmaticConfig.content_template || ''}
                        onChange={(e) => setProgrammaticConfig({ ...programmaticConfig, content_template: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        rows={4}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Multi-Language Tab */}
            {activeTab === 'hreflang' && (
              <div className="space-y-6">
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <h2 className="text-xl font-semibold text-white mb-6">Çoklu Dil SEO (Hreflang)</h2>
                  
                  <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg mb-6">
                    <div>
                      <p className="text-white font-medium">Çoklu Dili Etkinleştir</p>
                      <p className="text-zinc-500 text-sm">Uluslararası SEO için hreflang etiketleri ekle</p>
                    </div>
                    <Switch
                      checked={hreflangConfig.enabled || false}
                      onCheckedChange={(checked) => setHreflangConfig({ ...hreflangConfig, enabled: checked })}
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Varsayılan Dil</Label>
                      <Input
                        value={hreflangConfig.default_language || 'tr'}
                        onChange={(e) => setHreflangConfig({ ...hreflangConfig, default_language: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white w-32"
                        placeholder="tr"
                      />
                    </div>

                    <div>
                      <Label className="text-zinc-300 mb-3 block">Yapılandırılmış Diller</Label>
                      <div className="space-y-2">
                        {(hreflangConfig.languages || []).map((lang, idx) => (
                          <div key={idx} className="flex items-center gap-4 bg-zinc-800 rounded-lg p-3">
                            <Input
                              value={lang.code}
                              onChange={(e) => {
                                const newLangs = [...hreflangConfig.languages];
                                newLangs[idx].code = e.target.value;
                                setHreflangConfig({ ...hreflangConfig, languages: newLangs });
                              }}
                              className="bg-zinc-700 border-zinc-600 text-white w-20"
                              placeholder="en"
                            />
                            <Input
                              value={lang.name}
                              onChange={(e) => {
                                const newLangs = [...hreflangConfig.languages];
                                newLangs[idx].name = e.target.value;
                                setHreflangConfig({ ...hreflangConfig, languages: newLangs });
                              }}
                              className="bg-zinc-700 border-zinc-600 text-white flex-1"
                              placeholder="İngilizce"
                            />
                            <Input
                              value={lang.url_prefix}
                              onChange={(e) => {
                                const newLangs = [...hreflangConfig.languages];
                                newLangs[idx].url_prefix = e.target.value;
                                setHreflangConfig({ ...hreflangConfig, languages: newLangs });
                              }}
                              className="bg-zinc-700 border-zinc-600 text-white w-32"
                              placeholder="/en"
                            />
                            {lang.is_default && <Badge className="bg-emerald-500/20 text-emerald-400">Varsayılan</Badge>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Rank Checker Tab */}
            {activeTab === 'rank-checker' && (
              <div className="space-y-6">
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-xl font-semibold text-white">Google Sıra Bulucu</h2>
                      <p className="text-zinc-500 text-sm mt-1">Anahtar kelimelerinizin Google'da kaçıncı sırada olduğunu takip edin</p>
                    </div>
                    <Button
                      onClick={checkAllRanks}
                      disabled={rankCheckingAll || rankKeywords.length === 0}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {rankCheckingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      {rankCheckingAll ? 'Kontrol Ediliyor...' : 'Tümünü Kontrol Et'}
                    </Button>
                  </div>

                  {/* API Key uyarısı */}
                  {(!globalSettings.google_custom_search_api_key || !globalSettings.google_custom_search_cx) && (
                    <div className="flex items-center gap-3 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg mb-6">
                      <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                      <div>
                        <p className="text-yellow-400 font-medium">API Yapılandırması Gerekli</p>
                        <p className="text-yellow-400/70 text-sm">Sıra takibi için Google Custom Search API Key ve CX ID'yi "Genel Ayarlar" sekmesinden yapılandırın.</p>
                      </div>
                    </div>
                  )}

                  {/* Yeni anahtar kelime ekleme */}
                  <div className="flex gap-3 mb-6">
                    <Input
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addRankKeyword()}
                      className="bg-zinc-800 border-zinc-700 text-white flex-1"
                      placeholder="Anahtar kelime girin..."
                    />
                    <Button onClick={addRankKeyword} className="bg-emerald-600 hover:bg-emerald-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Ekle
                    </Button>
                  </div>

                  {/* Sonuç tablosu */}
                  {rankKeywords.length === 0 ? (
                    <div className="text-center py-12">
                      <Search className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                      <p className="text-zinc-400">Henüz takip edilen anahtar kelime yok</p>
                      <p className="text-zinc-500 text-sm mt-1">Yukarıdan bir anahtar kelime ekleyerek başlayın</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-zinc-800">
                            <th className="text-left text-zinc-400 text-sm font-medium py-3 px-4">Anahtar Kelime</th>
                            <th className="text-center text-zinc-400 text-sm font-medium py-3 px-4">Sıra</th>
                            <th className="text-left text-zinc-400 text-sm font-medium py-3 px-4">URL</th>
                            <th className="text-left text-zinc-400 text-sm font-medium py-3 px-4">Son Kontrol</th>
                            <th className="text-right text-zinc-400 text-sm font-medium py-3 px-4">İşlemler</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rankKeywords.map((kw) => (
                            <tr key={kw.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                              <td className="py-3 px-4">
                                <span className="text-white font-medium">{kw.keyword}</span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                {kw.last_position === null || kw.last_position === undefined ? (
                                  kw.last_checked ? (
                                    <span className="text-zinc-500 text-sm">100+</span>
                                  ) : (
                                    <span className="text-zinc-600 text-sm">—</span>
                                  )
                                ) : (
                                  <span className={`inline-flex items-center justify-center w-10 h-10 rounded-lg font-bold text-lg ${
                                    kw.last_position <= 3 ? 'bg-emerald-500/20 text-emerald-400' :
                                    kw.last_position <= 10 ? 'bg-yellow-500/20 text-yellow-400' :
                                    kw.last_position <= 30 ? 'bg-orange-500/20 text-orange-400' :
                                    'bg-red-500/20 text-red-400'
                                  }`}>
                                    {kw.last_position}
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4">
                                {kw.last_url ? (
                                  <a href={kw.last_url} target="_blank" rel="noopener noreferrer"
                                    className="text-emerald-400 hover:text-emerald-300 text-sm flex items-center gap-1 max-w-[300px] truncate">
                                    {kw.last_url.replace(/^https?:\/\//, '').substring(0, 50)}
                                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                  </a>
                                ) : (
                                  <span className="text-zinc-600 text-sm">—</span>
                                )}
                              </td>
                              <td className="py-3 px-4">
                                {kw.last_checked ? (
                                  <span className="text-zinc-400 text-sm">
                                    {new Date(kw.last_checked).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                ) : (
                                  <span className="text-zinc-600 text-sm">Henüz kontrol edilmedi</span>
                                )}
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => checkKeywordRank(kw.id)}
                                    disabled={rankChecking === kw.id}
                                    className="text-zinc-400 hover:text-white"
                                    title="Sıralamayı kontrol et"
                                  >
                                    {rankChecking === kw.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <RefreshCw className="w-4 h-4" />
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => deleteRankKeyword(kw.id)}
                                    className="text-zinc-400 hover:text-red-400"
                                    title="Sil"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Bilgi kartı */}
                <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-6">
                  <h3 className="text-white font-medium mb-3">Nasıl Çalışır?</h3>
                  <div className="space-y-2 text-zinc-400 text-sm">
                    <p>1. "Genel Ayarlar" sekmesinden Google Custom Search API Key, CX ID ve sitenizin domain adresini girin.</p>
                    <p>2. Takip etmek istediğiniz anahtar kelimeleri ekleyin.</p>
                    <p>3. "Tümünü Kontrol Et" veya tek tek kontrol butonlarını kullanarak sıralamaları öğrenin.</p>
                    <p className="text-zinc-500 mt-2">Not: Google Custom Search API ücretsiz planda günlük 100 sorgu hakkı sunar. Her anahtar kelime kontrolü en fazla 10 sorgu kullanır.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const NewRedirectForm = ({ onSubmit, onCancel }) => {
  const [data, setData] = useState({ source_url: '', target_url: '', redirect_type: 301, is_active: true });
  
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-zinc-300">Kaynak URL</Label>
        <Input
          value={data.source_url}
          onChange={(e) => setData({ ...data, source_url: e.target.value })}
          className="bg-zinc-800 border-zinc-700 text-white"
          placeholder="/old-page"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-zinc-300">Hedef URL</Label>
        <Input
          value={data.target_url}
          onChange={(e) => setData({ ...data, target_url: e.target.value })}
          className="bg-zinc-800 border-zinc-700 text-white"
          placeholder="/new-page"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-zinc-300">Yönlendirme Türü</Label>
        <Select value={String(data.redirect_type)} onValueChange={(v) => setData({ ...data, redirect_type: parseInt(v) })}>
          <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="301" className="text-zinc-300">301 (Kalıcı)</SelectItem>
            <SelectItem value="302" className="text-zinc-300">302 (Geçici)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex justify-end gap-3 pt-4">
        <Button variant="outline" onClick={onCancel} className="border-zinc-700 text-zinc-300">İptal</Button>
        <Button onClick={() => onSubmit(data)} className="bg-emerald-600 hover:bg-emerald-700">Oluştur</Button>
      </div>
    </div>
  );
};

const TemplateForm = ({ template, onSave, onCancel }) => {
  const [data, setData] = useState(template);
  
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-zinc-300">Şablon Adı</Label>
        <Input
          value={data.name}
          onChange={(e) => setData({ ...data, name: e.target.value })}
          className="bg-zinc-800 border-zinc-700 text-white"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-zinc-300">Sayfa Türü</Label>
        <Select value={data.page_type} onValueChange={(v) => setData({ ...data, page_type: v })}>
          <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="listing" className="text-zinc-300">İlan</SelectItem>
            <SelectItem value="category" className="text-zinc-300">Kategori</SelectItem>
            <SelectItem value="city" className="text-zinc-300">Şehir</SelectItem>
            <SelectItem value="blog" className="text-zinc-300">Blog</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="text-zinc-300">Başlık Şablonu</Label>
        <Input
          value={data.title_template}
          onChange={(e) => setData({ ...data, title_template: e.target.value })}
          className="bg-zinc-800 border-zinc-700 text-white"
          placeholder="{name} - {category} | {site_name}"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-zinc-300">Açıklama Şablonu</Label>
        <Textarea
          value={data.description_template}
          onChange={(e) => setData({ ...data, description_template: e.target.value })}
          className="bg-zinc-800 border-zinc-700 text-white"
          rows={3}
        />
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={data.is_default} onCheckedChange={(checked) => setData({ ...data, is_default: checked })} />
        <Label className="text-zinc-300">Bu sayfa türü için varsayılan olarak ayarla</Label>
      </div>
      <div className="flex justify-end gap-3 pt-4">
        <Button variant="outline" onClick={onCancel} className="border-zinc-700 text-zinc-300">İptal</Button>
        <Button onClick={() => onSave(data)} className="bg-emerald-600 hover:bg-emerald-700">Kaydet</Button>
      </div>
    </div>
  );
};

const SchemaForm = ({ schema, onSave, onCancel }) => {
  const [data, setData] = useState(schema);
  const [jsonError, setJsonError] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const validateJson = (val) => {
    try {
      JSON.parse(val);
      setJsonError('');
      return true;
    } catch (e) {
      setJsonError(e.message);
      return false;
    }
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(data.template);
      setData({ ...data, template: JSON.stringify(parsed, null, 2) });
      setJsonError('');
    } catch (e) {
      setJsonError(e.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-zinc-300">Şema Adı</Label>
          <Input
            value={data.name}
            onChange={(e) => setData({ ...data, name: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white"
            placeholder="Örn: Ana Sayfa Organizasyon"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-300">Şema Türü</Label>
          <Select value={data.schema_type} onValueChange={(v) => setData({ ...data, schema_type: v })}>
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="Organization" className="text-zinc-300">Organizasyon</SelectItem>
              <SelectItem value="LocalBusiness" className="text-zinc-300">Yerel İşletme</SelectItem>
              <SelectItem value="WebSite" className="text-zinc-300">Web Sitesi</SelectItem>
              <SelectItem value="Article" className="text-zinc-300">Makale</SelectItem>
              <SelectItem value="FAQPage" className="text-zinc-300">SSS Sayfası</SelectItem>
              <SelectItem value="HowTo" className="text-zinc-300">Nasıl Yapılır</SelectItem>
              <SelectItem value="Product" className="text-zinc-300">Ürün</SelectItem>
              <SelectItem value="Service" className="text-zinc-300">Hizmet</SelectItem>
              <SelectItem value="Person" className="text-zinc-300">Kişi</SelectItem>
              <SelectItem value="BreadcrumbList" className="text-zinc-300">BreadcrumbList</SelectItem>
              <SelectItem value="Event" className="text-zinc-300">Etkinlik</SelectItem>
              <SelectItem value="Review" className="text-zinc-300">İnceleme</SelectItem>
              <SelectItem value="VideoObject" className="text-zinc-300">Video</SelectItem>
              <SelectItem value="JobPosting" className="text-zinc-300">İş İlanı</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-zinc-300">JSON-LD Şablonu</Label>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={formatJson} className="text-zinc-400 hover:text-white h-7 text-xs">
              Formatla
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setShowPreview(!showPreview)} className="text-zinc-400 hover:text-white h-7 text-xs">
              {showPreview ? 'Düzenle' : 'Önizle'}
            </Button>
          </div>
        </div>

        {showPreview ? (
          <div className="bg-zinc-950 border border-zinc-700 rounded-lg p-4 min-h-[300px]">
            <div className="text-zinc-500 text-xs mb-2 font-mono">{'<script type="application/ld+json">'}</div>
            <pre className="text-emerald-400 text-sm font-mono whitespace-pre-wrap overflow-x-auto">
              {(() => { try { return JSON.stringify(JSON.parse(data.template), null, 2); } catch { return data.template; } })()}
            </pre>
            <div className="text-zinc-500 text-xs mt-2 font-mono">{'</script>'}</div>
          </div>
        ) : (
          <Textarea
            value={data.template}
            onChange={(e) => { setData({ ...data, template: e.target.value }); validateJson(e.target.value); }}
            className={`bg-zinc-800 border-zinc-700 text-white font-mono text-sm ${jsonError ? 'border-red-500/50' : ''}`}
            rows={14}
            placeholder='{"@context": "https://schema.org", "@type": "Organization", ...}'
          />
        )}

        {jsonError && (
          <p className="text-red-400 text-xs flex items-center gap-1">
            <span className="w-1 h-1 rounded-full bg-red-400" />
            JSON Hatası: {jsonError}
          </p>
        )}
      </div>

      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-2">
          <Switch checked={data.is_active} onCheckedChange={(checked) => setData({ ...data, is_active: checked })} />
          <Label className="text-zinc-300">Aktif</Label>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onCancel} className="border-zinc-700 text-zinc-300">İptal</Button>
          <Button onClick={() => onSave(data)} className="bg-emerald-600 hover:bg-emerald-700" disabled={!!jsonError}>Kaydet</Button>
        </div>
      </div>
    </div>
  );
};

export default SEOManagementPage;
